import { Component } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { SHARED_COMPONENTS } from '../../../shared/shared-imports';
import { AuthService } from '../../../core/services/auth.service';
import { environment } from '../../../../environments/environment';
import { SharedService } from '../../../shared/services/shared.service';
import { customValidator } from '../../../Utils/validator';
import { ApiService } from '../../../core/services/api.service';
import { ApiEndPoints, ApiRoot } from '../../../shared/constants/api-endpoints';
import { LookupKey } from '../../../shared/constants/lookup-keys';
import { Utils } from '../../../Utils/utils';
import { UserModel } from '../../../shared/models/loginuser.model';
import { RoutesPath } from '../../../shared/constants/routes-path';
import { DcMethodValues } from '../../../shared/constants/dc-method.constants';
import { FieldValue } from '../../../shared/constants/services.enum';

type Section = 'info' | 'password' | 'noti';
type NotificationFlagKey =
  | 'DC Email Notification Flg'
  | 'DC SMS Notification Flg'
  | 'DC App Updates Flg'
  | 'DC Latest News Flg';

interface NotiSetting {
  labelKey: string;
  fieldKey: NotificationFlagKey;
  enabled: boolean;
  updating?: boolean;
}

@Component({
  selector: 'app-profile',
  imports: [TranslateModule, ReactiveFormsModule, ...SHARED_COMPONENTS],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
})
export class ProfileComponent {
  private pendingProfilePayload: any = null;
  private emailChangeSubscription?: Subscription;
  private readonly notificationFieldMap: {
    labelKey: string;
    fieldKey: NotificationFlagKey;
  }[] = [
    { labelKey: 'PROFILE.NOTI.EMAIL', fieldKey: 'DC Email Notification Flg' },
    { labelKey: 'PROFILE.NOTI.SMS', fieldKey: 'DC SMS Notification Flg' },
    { labelKey: 'PROFILE.NOTI.UPDATES', fieldKey: 'DC App Updates Flg' },
    { labelKey: 'PROFILE.NOTI.LATEST_NEWS', fieldKey: 'DC Latest News Flg' },
  ];

  notificationsList: NotiSetting[] = [];
  active: Section = 'info';
  form!: FormGroup;
  pwdForm!: FormGroup;
  loginUserDetail: UserModel = {} as UserModel;
  updateProfileModal = false;
  lookupKey = LookupKey;
  formSubmitLoader = false;
  passwordSubmitLoader = false;
  flags = {
    notificationsSettingsEnabled:
      environment.mVP1Settings?.notificationsSettingsEnabled ?? true,
  };
  lang = 'en';
  loginUserName = '';
  showErrorMsg = false;
  errorMessage = '';
  showPasswordErrorMsg = false;
  passwordErrorMessage = '';
  emailVerificationPending = false;
  pendingVerificationEmail = '';
  duplicateEmailCheckLoader = false;
  verifiedEmail = '';
  emailLockedForVerification = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private http: HttpClient,
    private authService: AuthService,
    private sharedService: SharedService,
    private api: ApiService
  ) {
    this.lang = this.sharedService.getCurrentLang();
    const storedProfile = this.sharedService.getUserProfile();
    this.loginUserDetail = storedProfile ? storedProfile : ({} as UserModel);
    this.loginUserName =
      this.lang === 'ar'
        ? this.loginUserDetail['Name Arabic']
        : this.loginUserDetail['Name English'];
    this.initializeNotificationSettings();
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['tab'] === 'notifications') {
        this.active = 'noti';
      }
    });

    this.form = this.fb.group({
      firstName: [
        '',
        [Validators.required, Validators.maxLength(150), customValidator('safeText')],
      ],
      lastName: [
        '',
        [Validators.required, Validators.maxLength(150), customValidator('safeText')],
      ],
      email: ['', [Validators.required, customValidator('email')]],
      emiratesId: ['', [customValidator('emiratesId')]],
      phone: ['', [Validators.required, customValidator('mobile')]],
      countryCode: ['', Validators.required],
      country: [null, Validators.required],
    });

    this.pwdForm = this.fb.group({
      old: ['', Validators.required],
      new: ['', Validators.required],
      confirm: ['', Validators.required],
    });

    this.emailChangeSubscription = this.form
      .get('email')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((email) => this.handleEmailChange(email));

    this.getUserData();
  }

  ngOnDestroy(): void {
    this.emailChangeSubscription?.unsubscribe();
  }

  switch(sec: Section) {
    if (sec === 'password') {
      this.pwdForm.reset();
      this.showPasswordErrorMsg = false;
      this.passwordErrorMessage = '';
    }
    this.active = sec;
  }

  signOut() {
    this.authService.logout();
  }

  gotoContactUs() {
    this.router.navigate([RoutesPath.CONTACT_US]);
  }

  openUpdateProfileModal() {
    this.showErrorMsg = false;
    this.errorMessage = '';
    this.emailVerificationPending = false;
    this.pendingVerificationEmail = '';
    this.verifiedEmail = '';
    this.pendingProfilePayload = null;
    this.unlockEmailField();
    this.updateProfileModal = true;
  }

  updateProfile() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formData = this.form.getRawValue();
    this.showErrorMsg = false;
    this.errorMessage = '';
    this.emailVerificationPending = false;
    this.pendingVerificationEmail = '';
    this.pendingProfilePayload = this.buildProfilePayload(formData);
    this.checkDuplicateEmailAndProceed(formData.email);
  }

  getUserData() {
    if (this.authService.isLoggedIn()) {
      const user = this.authService.getUserClaims();
      if (user) {
        const requestPayload = {
          body: {
            "ProcessName": "DC Get User Profile Details Web WF",
            "DC Channel": this.sharedService.getCurrentLang() == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
            "DC Method": DcMethodValues.PROFILE,
            "DCChannel": "" //"Mobile"
          },
        };

        this.api
          .post(
            ApiRoot.DCCICPIntegration,
            ApiEndPoints.GET_USER_PROFILE_RUN_PROCESS,
            requestPayload
          )
          .subscribe({
            next: (response: any) => {
              if (response?.SiebelMessage.User) {
                this.sharedService.setUserProfile(response?.SiebelMessage.User);
                this.loginUserDetail = this.sharedService.getUserProfile();
                this.loginUserName =
                  this.lang === 'ar'
                    ? this.loginUserDetail['Name Arabic']
                    : this.loginUserDetail['Name English'];
                this.form.patchValue({
                  firstName: this.loginUserDetail['First Name'],
                  lastName: this.loginUserDetail['Last Name'],
                  email: this.loginUserDetail['EMail Addr'],
                  phone: this.loginUserDetail['Phone #']
                    ? Utils.parseMobile(this.loginUserDetail['Phone #'], 'number')
                    : '',
                  emiratesId: this.loginUserDetail['Emirates Id'],
                  country: this.loginUserDetail['DC Nationality'],
                  countryCode: this.loginUserDetail['Phone #']
                    ? Utils.parseMobile(this.loginUserDetail['Phone #'], 'cc')
                    : '',
                });
                this.initializeNotificationSettings();
              }
            },
          });
      }
    }
  }

  UpdateNotifictionSettings(setting: NotiSetting) {
    if (setting?.updating) {
      return;
    }

    const newValue = !setting.enabled;
    setting.updating = true;
    const requestPayload = this.buildNotificationPayload(setting.fieldKey, newValue);

    this.api
      .post(ApiRoot.DCCICPIntegration, ApiEndPoints.RUN_PROCESS, requestPayload)
      .subscribe({
        next: (response: any) => {
          setting.updating = false;
          if (response['Error Code'] == '' && response['Error Message'] == '') {
            setting.enabled = newValue;
            this.loginUserDetail[setting.fieldKey] = newValue ? 'Y' : 'N';
            this.sharedService.setUserProfile(this.loginUserDetail);
          }
        },
        error: () => {
          setting.updating = false;
        },
      });
  }

  changePassword() {
    if (this.pwdForm.invalid) {
      this.pwdForm.markAllAsTouched();
      return;
    }

    const username =
      this.authService.getUserClaims()?.username ||
      this.loginUserDetail?.['Login Name'];
    const { old, new: newPassword, confirm } = this.pwdForm.getRawValue();

    if (newPassword !== confirm) {
      this.pwdForm.get('confirm')?.setErrors({ password_mismatch: true });
      return;
    }

    this.showPasswordErrorMsg = false;
    this.passwordErrorMessage = '';
    this.passwordSubmitLoader = true;

    this.api
      .post('wso2API', 'DCAuthToken/Recovery/ChangePassword', {
        username,
        password: old,
        new_password: newPassword,
      })
      .subscribe({
        next: (response: any) => {
          this.passwordSubmitLoader = false;
          if (response?.status == '400' || response?.status == '500') {
            this.showPasswordErrorMsg = true;
            this.passwordErrorMessage =
              response?.detail || 'Error while changing password.';
          } else {
            this.sharedService.toastMessage(
              'SUCCESS',
              'PWD_CHANGE_SUCCESSFUL',
              'success'
            );
            this.pwdForm.reset();
          }
        },
        error: (error: any) => {
          this.passwordSubmitLoader = false;
          this.showPasswordErrorMsg = true;
          this.passwordErrorMessage =
            error?.error?.detail || 'Error while changing password.';
        },
      });
  }

  private initializeNotificationSettings() {
    this.notificationsList = this.notificationFieldMap.map(
      ({ labelKey, fieldKey }) => ({
        labelKey,
        fieldKey,
        enabled: this.parseNotificationFlag(this.loginUserDetail?.[fieldKey]),
        updating: false,
      })
    );
  }

  private parseNotificationFlag(flag: string | boolean | null | undefined): boolean {
    if (typeof flag === 'string') {
      return flag.trim().toUpperCase() === 'Y';
    }
    return !!flag;
  }

  private buildNotificationPayload(
    changedKey: NotificationFlagKey,
    enabled: boolean
  ) {
    const flags = this.notificationFieldMap.reduce((acc, curr) => {
      const value =
        curr.fieldKey === changedKey
          ? enabled
          : this.parseNotificationFlag(this.loginUserDetail?.[curr.fieldKey]);
      acc[curr.fieldKey] = value ? 'Y' : 'N';
      return acc;
    }, {} as Record<NotificationFlagKey, string>);

    return {
      body: {
        "ProcessName": 'DC Update User Primay Account CSN',
        "Account Id": "",
        "DC Channel": this.sharedService.getCurrentLang() == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": "Set Notifications",
        ...flags,
      },
    };
  }

  onProfileEmailVerified(): void {
    this.verifiedEmail = this.normalizeEmail(this.pendingVerificationEmail);
    this.emailVerificationPending = false;
    this.pendingVerificationEmail = '';
    this.unlockEmailField();
    this.submitProfileUpdate();
  }

  onProfileEmailVerificationClosed(): void {
    this.emailVerificationPending = false;
    this.unlockEmailField();
  }

  onProfileEmailTimerCompleted(): void {
    this.unlockEmailField();
  }

  onProfileEmailOtpResent(response: any): void {
    if (this.isOtpSuccessResponse(response)) {
      this.lockEmailField();
    }
  }
  private buildProfilePayload(formData: any) {
    const normalizedEmiratesId = formData.emiratesId
      ? formData.emiratesId.replace(/-/g, '')
      : '';
    const normalizedEmail = this.normalizeEmail(formData.email);
    const currentEmail = this.normalizeEmail(this.loginUserDetail?.['EMail Addr']);
    const operations: any[] = [
      {
        op: 'replace',
        value: {
          name: {
            givenName: formData.firstName,
            familyName: formData.lastName,
          },
        },
      },
      {
        op: 'replace',
        value: {
          'urn:ietf:params:scim:schemas:extension:enterprise:2.0:User': {
            country: formData.country,
          },
        },
      },
    ];

    if (normalizedEmail !== currentEmail) {
      operations.push({
        op: 'replace',
        value: {
          emails: [formData.email],
        },
      });
    }

    operations.push(
      {
        op: 'replace',
        value: {
          phoneNumbers: [
            {
              type: 'mobile',
              value: `${formData.countryCode}${formData.phone}`,
            },
          ],
        },
      },
      {
        op: 'replace',
        value: {
          title: '',
        },
      },
      {
        op: 'replace',
        path: 'urn:scim:wso2:schema:emiratesId',
        value: normalizedEmiratesId,
      }
    );

    return {
      schemas: ['urn:ietf:params:scim:api:messages:2.0:PatchOp'],
      Operations: operations,
    };
  }

  private checkDuplicateEmailAndProceed(email: string): void {
    const normalizedEmail = this.normalizeEmail(email);
    const params = new HttpParams().set('filter', `emails eq ${normalizedEmail}`);

    this.duplicateEmailCheckLoader = true;
    this.http
      .get<any>(`${environment.wso2APIURL}scim2/Users`, { params })
      .subscribe({
        next: (response: any) => {
          this.duplicateEmailCheckLoader = false;

          if (this.canProceedWithEmail(response, normalizedEmail)) {
            if (normalizedEmail && normalizedEmail === this.verifiedEmail) {
              this.submitProfileUpdate();
              return;
            }
            this.sendProfileEmailOtp(normalizedEmail);
            return;
          }

          const duplicateEmails = this.getDuplicateEmails(response);
          const duplicateUserNames = this.getDuplicateUserNames(response);
          this.showErrorMsg = true;
          this.errorMessage = `the email ${this.maskDuplicateEmail(normalizedEmail)} is already linked to another user.<br>Usernames: ${duplicateUserNames || '-'}<br>Emails: ${duplicateEmails || '-'}`;
        },
        error: (error: any) => {
          this.duplicateEmailCheckLoader = false;
          this.showErrorMsg = true;
          this.errorMessage =
            error?.error?.detail || 'Error while validating email.';
        },
      });
  }

  private canProceedWithEmail(response: any, normalizedEmail: string): boolean {
    const totalResults = Number(response?.totalResults || 0);
    const resources = Array.isArray(response?.Resources) ? response.Resources : [];

    if (totalResults === 0) {
      return true;
    }

    if (totalResults === 1) {
      const resource = resources[0];
      const resourceEmails = Array.isArray(resource?.emails)
        ? resource.emails.map((item: string) => this.normalizeEmail(item))
        : [];
      const currentEmail = this.normalizeEmail(this.loginUserDetail?.['EMail Addr']);
      const currentUsername = this.normalizeEmail(
        this.loginUserDetail?.['Login Name']
      );
      const resourceUsername = this.normalizeEmail(resource?.userName);

      return (
        resourceEmails.includes(normalizedEmail) &&
        resourceEmails.includes(currentEmail) &&
        resourceUsername === currentUsername
      );
    }

    return false;
  }

  private sendProfileEmailOtp(email: string): void {
    const loginName = this.normalizeEmail(
      this.authService.getUserClaims()?.username ||
        this.loginUserDetail?.['Login Name']
    );

    const requestPayload = {
      body: {
        ProcessName: 'DC CP Email Verification Process WF',
        LoginName: loginName,
        EmailAddr: email,
        ReGenOTPFlg: 'N',
      },
    };

    this.formSubmitLoader = true;
    this.api
      .post(
        ApiRoot.DCCICPIntegration,
        ApiEndPoints.GENERATE_EMAIL_OTP,
        requestPayload
      )
      .subscribe({
        next: (response: any) => {
          this.formSubmitLoader = false;
          if (this.isOtpSuccessResponse(response)) {
            this.verifiedEmail = '';
            this.emailVerificationPending = true;
            this.pendingVerificationEmail = email;
            this.lockEmailField();
            return;
          }

          this.showErrorMsg = true;
          this.errorMessage =
            response?.['Error Message'] || 'Error while sending OTP.';
        },
        error: (error: any) => {
          this.formSubmitLoader = false;
          this.showErrorMsg = true;
          this.errorMessage =
            error?.error?.detail || 'Error while sending OTP.';
        },
      });
  }

  private submitProfileUpdate(): void {
    if (!this.pendingProfilePayload) {
      return;
    }

    this.formSubmitLoader = true;
    this.api
      .updateProfile(
        ApiRoot.scim2,
        ApiEndPoints.UPDATE_PROFILE,
        this.pendingProfilePayload
      )
      .subscribe({
        next: (response: any) => {
          if (response.status) {
            this.showErrorMsg = true;
            this.errorMessage = response.detail || '';
            this.formSubmitLoader = false;
          } else {
            this.formSubmitLoader = false;
            this.updateProfileModal = false;
            this.errorMessage = '';
            this.pendingProfilePayload = null;
            this.sharedService.toastMessage(
              'SUCCESS',
              'SUBMITTED_SUCCESSFUL',
              'success'
            );
            this.getUserData();
          }
        },
        error: (error: any) => {
          this.formSubmitLoader = false;
          this.showErrorMsg = true;
          this.errorMessage =
            error?.error?.detail || 'Error while updating profile.';
        },
      });
  }

  private normalizeEmail(value: string): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private handleEmailChange(email: string): void {
    const emailControl = this.form.get('email');
    const normalizedEmail = this.normalizeEmail(email);
    const currentEmail = this.normalizeEmail(this.loginUserDetail?.['EMail Addr']);

    if (!normalizedEmail || normalizedEmail === currentEmail) {
      this.emailVerificationPending = false;
      this.pendingVerificationEmail = '';
      this.verifiedEmail = '';
      this.pendingProfilePayload = null;
      this.showErrorMsg = false;
      this.errorMessage = '';
      this.unlockEmailField();
      return;
    }

    if (emailControl?.invalid) {
      this.emailVerificationPending = false;
      this.pendingVerificationEmail = '';
      this.verifiedEmail = '';
      this.pendingProfilePayload = null;
      this.unlockEmailField();
      return;
    }

    if (
      normalizedEmail === this.verifiedEmail ||
      normalizedEmail === this.pendingVerificationEmail
    ) {
      return;
    }

    this.pendingProfilePayload = null;
    this.showErrorMsg = false;
    this.errorMessage = '';
    this.checkDuplicateEmailAndProceed(normalizedEmail);
  }

  private lockEmailField(): void {
    if (this.emailLockedForVerification) {
      return;
    }

    this.emailLockedForVerification = true;
    this.form.get('email')?.disable({ emitEvent: false });
  }

  private unlockEmailField(): void {
    if (!this.form?.get('email')) {
      this.emailLockedForVerification = false;
      return;
    }

    this.emailLockedForVerification = false;
    this.form.get('email')?.enable({ emitEvent: false });
  }

  private maskDuplicateEmail(email: string): string {
    const localPart = this.normalizeEmail(email).split('@')[0] || '';
    return `${localPart}****`;
  }

  private isOtpSuccessResponse(response: any): boolean {
    const errorCode = response?.['Error Code'];
    const errorMsg = response?.['Error Message'];
    const codeOk =
      errorCode === 0 || errorCode === '0' || errorCode === '' || errorCode == null;
    const msgOk = errorMsg === '' || errorMsg == null;
    return codeOk && msgOk;
  }

  private getDuplicateEmails(response: any): string {
    const resources = Array.isArray(response?.Resources) ? response.Resources : [];
    const emails = resources.flatMap((resource: any) => {
      const resourceEmails = Array.isArray(resource?.emails) ? resource.emails : [];
      return resourceEmails.map((email: string) => this.normalizeEmail(email));
    });

    return [...new Set(emails)].join(', ');
  }

  private getDuplicateUserNames(response: any): string {
    const resources = Array.isArray(response?.Resources) ? response.Resources : [];
    const userNames = resources
      .map((resource: any) => this.normalizeEmail(resource?.userName))
      .filter((userName: string) => !!userName);

    return [...new Set(userNames)].join(', ');
  }
}
